// EnhancedAnalyticsDashboard.tsx - Advanced Classification Analytics Dashboard
//
// 🎯 VISION: Comprehensive Analytics Dashboard for Document Classification
// This dashboard provides:
// - 📊 Advanced classification metrics and performance indicators
// - 🌍 Multilingual analytics and language distribution
// - ⚡ Processing performance and success rates
// - 🔄 Workflow analytics and automation insights
// - 🤖 AI enhancement metrics and capabilities
// - 📈 Interactive charts and visualizations
// - 🎨 Modern, responsive UI with dark mode support

import React, { useState, useEffect } from 'react';
import {
  DocumentTextIcon,
  GlobeAltIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  PlayIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { useDocumentAPI } from '../DocumentAPI';
import { SystemPerformancePanel } from './SystemPerformancePanel';

interface EnhancedAnalyticsDashboardProps {
  className?: string;
}

interface AnalyticsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className="flex items-center mt-2">
              <ArrowTrendingUpIcon 
                className={`w-4 h-4 ${trend.isPositive ? 'text-green-500' : 'text-red-500'}`} 
              />
              <span className={`text-sm font-medium ml-1 ${
                trend.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {trend.isPositive ? '+' : ''}{trend.value}%
              </span>
            </div>
          )}
        </div>
        {Icon && (
          <div className="p-3 rounded-lg">
            <Icon className="w-6 h-6" />
          </div>
        )}
      </div>
    </div>
  );
};

// We'll store the actual analytics object, not the typed DocumentAnalyticsResponse
const EnhancedAnalyticsDashboard: React.FC<EnhancedAnalyticsDashboardProps> = ({ className = '' }) => {
  const [analytics, setAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const documentAPI = useDocumentAPI();

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await documentAPI.getComprehensiveAnalyticsReport();
      setAnalytics(data && (data as any).classification_analytics ? data : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line
  }, [refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleExport = async () => {
    try {
      await documentAPI.exportAnalyticsReport({ format: 'csv' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export analytics');
    }
  };

  if (isLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-gray-200 dark:bg-gray-700 h-32 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className}`}>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Error Loading Analytics
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Always show System Performance Panel regardless of analytics data availability
  if (!analytics || !analytics.classification_analytics) {
    return (
      <div className={`space-y-6 ${className}`}>
        {/* System Performance Panel - Always Available */}
        <SystemPerformancePanel />
        
        {/* No Analytics Data Message */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                No Document Analytics Data Available
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Document analytics data will appear here once documents are processed and classified.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { classification_analytics } = analytics;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Document Analytics Dashboard
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Comprehensive insights into document classification and processing performance
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <PlayIcon className="w-6 h-6 text-lex-accent" />
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            AI-Powered Analytics
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center space-x-3">
        <button
          onClick={handleRefresh}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent"
        >
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          Refresh
        </button>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent"
        >
          <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* System Performance Panel */}
      <SystemPerformancePanel />

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <AnalyticsCard
          title="Total Documents"
          value={classification_analytics.total_documents}
          subtitle="All processed documents"
          icon={DocumentTextIcon}
        />
        <AnalyticsCard
          title="Classified Documents"
          value={classification_analytics.classified_documents}
          subtitle={`${((classification_analytics.classified_documents / classification_analytics.total_documents) * 100).toFixed(1)}% success rate`}
          icon={CheckCircleIcon}
        />
        <AnalyticsCard
          title="Multilingual Documents"
          value={classification_analytics.multilingual_metrics.total_multilingual}
          subtitle="Non-English documents"
          icon={GlobeAltIcon}
        />
        <AnalyticsCard
          title="Processing Time"
          value={`${classification_analytics.processing_performance.average_processing_time.toFixed(1)}s`}
          subtitle="Average per document"
          icon={ClockIcon}
        />
      </div>

      {/* Classification Performance */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Classification Performance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-lex-accent">
              {classification_analytics.classification_performance.accuracy.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Accuracy</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {classification_analytics.classification_performance.confidence.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Average Confidence</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {classification_analytics.classification_performance.completion_rate.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Completion Rate</div>
          </div>
        </div>
      </div>

      {/* Language Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Language Distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(classification_analytics.multilingual_metrics.language_distribution).map(([language, count]) => (
            <div key={language} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center">
                <GlobeAltIcon className="w-5 h-5 text-lex-accent mr-2" />
                <span className="font-medium text-gray-900 dark:text-white capitalize">
                  {language}
                </span>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">{String(count)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Document Types Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Document Types Distribution
        </h3>
        <div className="space-y-3">
          {Object.entries(classification_analytics.document_types_distribution)
            .sort(([,a], [,b]) => Number(b) - Number(a))
            .slice(0, 10)
            .map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <div className="flex items-center">
                  <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <span className="text-gray-900 dark:text-white capitalize">
                    {type.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-lex-accent h-2 rounded-full"
                      style={{ 
                        width: `${(Number(count) / classification_analytics.total_documents) * 100}%` 
                      }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400 w-12 text-right">
                    {String(count)}
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Workflow Analytics */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Workflow Analytics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-lex-accent">
              {classification_analytics.workflow_analytics.templates_generated}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Templates Generated</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-pink-600 dark:text-pink-400">
              {classification_analytics.workflow_analytics.matter_assignments}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Matter Assignments</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
              {classification_analytics.workflow_analytics.workflow_triggers}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Workflow Triggers</div>
          </div>
        </div>
      </div>

      {/* AI Enhancement Metrics */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          AI Enhancement Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
              Processing Enhancements
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Metadata Extraction</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {classification_analytics.ai_enhancements.metadata_extraction_accuracy.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Party Detection</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {classification_analytics.ai_enhancements.party_detection_accuracy.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Context Analysis</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {classification_analytics.ai_enhancements.context_analysis_accuracy.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
              Performance Metrics
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Processing Speed</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {classification_analytics.ai_enhancements.processing_speed_improvement.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Error Reduction</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {classification_analytics.ai_enhancements.error_reduction_rate.toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">User Satisfaction</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {classification_analytics.ai_enhancements.user_satisfaction_score.toFixed(1)}/5
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedAnalyticsDashboard; 